import { resolveRecipeFlags } from './context';
import { resolveVisibleInputs } from './inputs';
import { isEmptyValue, resolveMissingInputs } from './validation';
import { CsRecipeInput, RecipeSchemaContext } from './types';

export interface CoercionReport {
    /** Inputs safe to submit — only keys the recipe actually declares. */
    inputs: Record<string, any>;
    /** Keys discarded because the recipe has no such input for this product. */
    dropped: Array<{ key: string; reason: 'unknown' | 'not-applicable' }>;
    /** Values replaced because they weren't in the option list. */
    snapped: Array<{ key: string; from: any; to: any }>;
    /** Required keys filled from their declared default. */
    defaulted: string[];
    /** Required keys still empty — no default existed to fall back on. */
    missing: string[];
}

/** Keys the UI stores on the node but which aren't part of the recipe schema. */
const CONTROL_KEYS = new Set([
    'mode', 'autoPick', 'use3dCamera', 'surfaceMode', 'appliedEnvironment', 'compose',
    // Per-route stashes, so switching Compose does not discard the other
    // route's capture. UI bookkeeping — never a recipe input.
    'studioSnapshotUrl', 'moodboardSnapshotUrl', 'studioCameraView', 'studioFurnitureRoom',
]);

/** Case- and separator-insensitive match, so "Living Room" finds "living-room". */
function normalize(value: any): string {
    return String(value).trim().toLowerCase().replace(/[\s_]+/g, '-');
}

function findOption(input: CsRecipeInput, value: any): string | undefined {
    if (!input.options || input.options.length === 0) return undefined;
    const exact = input.options.find((o) => o.value === value);
    if (exact) return exact.value;
    const target = normalize(value);
    const loose = input.options.find(
        (o) => normalize(o.value) === target || normalize(o.label) === target,
    );
    return loose?.value;
}

/**
 * Force a proposed set of inputs onto what a recipe actually accepts.
 *
 * Written for the planner: a language model asked to configure a recipe will
 * confidently invent option values, use a label where a slug belongs, and set
 * fields that don't apply to the linked product. Rather than rejecting the
 * whole plan, this snaps what it can, drops what it can't, and reports both —
 * so the vendor sees a working recipe node and we can log what the model got
 * wrong.
 *
 * Enum values are never invented: a value that matches no option falls back to
 * the input's declared default, or is dropped when there is none.
 */
export function coerceRecipeInputs(
    ctx: RecipeSchemaContext,
    proposed: Record<string, any>,
): CoercionReport {
    const report: CoercionReport = {
        inputs: {}, dropped: [], snapped: [], defaulted: [], missing: [],
    };

    // Resolve visibility against the proposed values, since control keys like
    // `mode` and `surfaceMode` change which inputs exist at all.
    const seeded: RecipeSchemaContext = { ...ctx, inputs: { ...ctx.inputs, ...proposed } };
    const visible = resolveVisibleInputs(seeded);
    const byKey = new Map(visible.map((i) => [i.key, i]));

    // Control keys pass through untouched — they drive the schema rather than
    // being described by it.
    for (const key of CONTROL_KEYS) {
        if (proposed[key] !== undefined) report.inputs[key] = proposed[key];
    }

    const declaredAnywhere = new Set((ctx.recipe.inputs || []).map((i) => i.key));

    for (const [key, value] of Object.entries(proposed)) {
        if (CONTROL_KEYS.has(key)) continue;

        const input = byKey.get(key);
        if (!input) {
            report.dropped.push({
                key,
                // Declared by the recipe but filtered out for this product's
                // visualization type — a different failure from a hallucination.
                reason: declaredAnywhere.has(key) ? 'not-applicable' : 'unknown',
            });
            continue;
        }

        if (input.options && input.options.length > 0) {
            const match = findOption(input, value);
            if (match === undefined) {
                if (input.default !== undefined) {
                    report.snapped.push({ key, from: value, to: input.default });
                    report.inputs[key] = input.default;
                } else {
                    report.dropped.push({ key, reason: 'unknown' });
                }
                continue;
            }
            if (match !== value) report.snapped.push({ key, from: value, to: match });
            report.inputs[key] = match;
            continue;
        }

        // Numbers get bounds where the recipe declares them. Enum values were
        // already protected; numbers passed straight through, so a camera
        // elevation of 200 would have been written verbatim and rendered a
        // nonsense shot.
        if (input.type === 'number' && (input.min !== undefined || input.max !== undefined)) {
            const asNumber = typeof value === 'number' ? value : parseFloat(String(value));
            if (Number.isNaN(asNumber)) {
                report.dropped.push({ key, reason: 'unknown' });
                continue;
            }
            const clamped = Math.min(
                input.max ?? asNumber,
                Math.max(input.min ?? asNumber, asNumber),
            );
            if (clamped !== asNumber) report.snapped.push({ key, from: value, to: clamped });
            report.inputs[key] = clamped;
            continue;
        }

        report.inputs[key] = value;
    }

    // Fill required gaps from declared defaults, then report what's still open.
    const flags = resolveRecipeFlags({ ...ctx, inputs: { ...ctx.inputs, ...report.inputs } });
    const finalVisible = resolveVisibleInputs({ ...ctx, inputs: { ...ctx.inputs, ...report.inputs } });

    for (const input of finalVisible) {
        if (!input.required) continue;
        if (!isEmptyValue(report.inputs[input.key])) continue;
        if (input.default !== undefined && !isEmptyValue(input.default)) {
            report.inputs[input.key] = input.default;
            report.defaulted.push(input.key);
        }
    }

    // Re-run the real validator so the planner's notion of "complete" is the
    // same one the Generate button uses — no second opinion.
    report.missing = resolveMissingInputs(
        { ...ctx, inputs: { ...ctx.inputs, ...report.inputs } },
        flags,
        finalVisible,
    );

    return report;
}
